

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Eye, SquarePen, Trash2  } from 'lucide-react';

// ── MAIN COMPONENT ─────────────────────────────────────
export default function LeadListPage() {
  const navigate = useNavigate();

  const [leads,         setLeads]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [deleteTarget,  setDeleteTarget]  = useState(null);  // lead object to delete
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [idSearch,      setIdSearch]      = useState("");    // search by ID
  const [phoneSearch,   setPhoneSearch]   = useState("");    // search by phone

  const user = JSON.parse(localStorage.getItem("user"));

  // ── FETCH ────────────────────────────────────────────
  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/leads");
      setLeads(response.data.data || []);
    } catch (error) {
      console.error("Fetch Leads Error:", error);
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []);

  // ── SEARCH FILTER ────────────────────────────────────
  const filteredLeads = leads.filter((lead) => {
    const idTerm = idSearch.trim().toLowerCase();
    const phoneTerm = phoneSearch.trim().toLowerCase();

    const idOk = !idTerm || String(lead.id).toLowerCase().includes(idTerm);
    const phoneOk = !phoneTerm || (lead.business_phone || "").toLowerCase().includes(phoneTerm);

    return idOk && phoneOk;
  });

  const hasActiveSearch = idSearch.trim() || phoneSearch.trim();

  // ── DELETE ───────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoading(true);
      await axiosInstance.delete(`/leads/${deleteTarget.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      toast.success(`Lead #${deleteTarget.id} deleted successfully.`);
      setDeleteTarget(null);
    } catch (error) {
      console.error("Delete Error:", error);
      toast.error("Failed to delete lead. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">

      {/* Toast container */}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      {/* Delete Modal */}
      {deleteTarget && (
        <DeleteModal
          lead={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          loading={deleteLoading}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-800">Leads</h1>
          <p className="text-sm text-slate-500 mt-1">Manage all created leads</p>
        </div>

        {(user.role === "admin" || user.role === "agent") && (
          <button
            onClick={() => navigate("/leads/new")}
            className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition"
          >
            + Create Lead
          </button>
        )}
      </div>

      {/* Search Bars */}
      <div className="flex flex-wrap gap-3 mb-4">

        {/* ID Search */}
        <div className="relative w-full sm:w-56">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
          </svg>
          <input
            type="text"
            value={idSearch}
            onChange={(e) => setIdSearch(e.target.value)}
            placeholder="Search by ID..."
            className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          />
          {idSearch && (
            <button
              onClick={() => setIdSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Phone Search */}
        <div className="relative w-full sm:w-56">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
          </svg>
          <input
            type="text"
            value={phoneSearch}
            onChange={(e) => setPhoneSearch(e.target.value)}
            placeholder="Search by phone..."
            className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          />
          {phoneSearch && (
            <button
              onClick={() => setPhoneSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Clear both */}
        {hasActiveSearch && (
          <button
            onClick={() => { setIdSearch(""); setPhoneSearch(""); }}
            className="px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 text-sm font-semibold transition"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Loading */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
          Loading leads...
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
          <p className="text-slate-500">
            {hasActiveSearch ? "No leads match your search" : "No leads found"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">

              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-5 py-4 font-semibold text-slate-600">ID</th>
                  <th className="text-left px-5 py-4 font-semibold text-slate-600">Business Name</th>
                  <th className="text-left px-5 py-4 font-semibold text-slate-600">Contact Name</th>
                  <th className="text-left px-5 py-4 font-semibold text-slate-600">Phone</th>
                  <th className="text-left px-5 py-4 font-semibold text-slate-600">Validation Type</th>
                  {user.role === "admin" && (
                    <th className="text-left px-5 py-4 font-semibold text-slate-600">Created By</th>
                  )}
                  <th className="text-left px-5 py-4 font-semibold text-slate-600">Created Date</th>
                  <th className="text-right px-5 py-4 font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} className="border-b border-slate-100 hover:bg-slate-50 transition">

                    <td className="px-5 py-4 font-medium text-slate-700">#{lead.id}</td>

                    <td className="px-5 py-4">
                      <div className="font-semibold text-slate-800">{lead.business_name || "-"}</div>
                      <div className="text-xs text-slate-500 mt-1">{lead.business_address || "-"}</div>
                    </td>

                    <td className="px-5 py-4 text-slate-600">{lead.contact_name || "-"}</td>

                    <td className="px-5 py-4 text-slate-600">{lead.business_phone || "-"}</td>

                    <td className="px-5 py-4">
                      <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 capitalize">
                        {lead.validation_type || "-"}
                      </span>
                    </td>

                    {user.role === "admin" && (
                      <td className="px-5 py-4 text-slate-500">{lead.created_by_name}</td>
                    )}

                    <td className="px-5 py-4 text-slate-500">
                      {lead.created_at
                        ? new Date(lead.created_at).toLocaleDateString("en-GB")
                        : "-"}
                    </td>

                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">

                        <button
                          onClick={() => navigate(`/leads/${lead.id}`)}
                          className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold"
                        >
                            <Eye />
                        </button>

                        {user.role === "admin" && (
                          <>
                            <button
                              onClick={() => navigate(`/leads/edit/${lead.id}`)}
                              className="px-2 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
                            >
                          <SquarePen />
                            </button>

                            <button
                              onClick={() => setDeleteTarget(lead)}
                              className="px-2 py-1 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold"
                            >
                            <Trash2 />
                            </button>
                          </>
                        )}

                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>

            </table>
          </div>
        </div>
      )}
    </div>
  );
}




// ── DELETE CONFIRMATION MODAL ──────────────────────────
const DeleteModal = ({ lead, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4 animate-fadeIn">

      {/* Icon */}
      <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mx-auto mb-4">
        <svg className="w-7 h-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0a1 1 0 00-1-1h-4a1 1 0 00-1 1H5a1 1 0 000 2h14a1 1 0 000-2h-1z" />
        </svg>
      </div>

      <h3 className="text-lg font-bold text-slate-800 text-center mb-1">
        Delete Lead
      </h3>
      <p className="text-sm text-slate-500 text-center mb-1">
        Are you sure you want to delete
      </p>
      <p className="text-sm font-semibold text-slate-700 text-center mb-6">
        #{lead.id} — {lead.business_name}?
      </p>
      <p className="text-xs text-red-500 text-center mb-6">
        This action cannot be undone.
      </p>

      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="flex-1 py-2.5 rounded-xl border border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm transition disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm transition disabled:opacity-60"
        >
          {loading ? "Deleting..." : "Yes, Delete"}
        </button>
      </div>

    </div>
  </div>
);